import {Button, message} from "antd";
import React, {FC} from "react";
import {SendOutlined} from "@ant-design/icons";
import {
    Booking,
    EditTemplateInput,
    GetBookingsByCourseDocument,
    SendInvoicesToSelectionOfBookingInput,
    SendMailToSelectionOfAttendeesInput,
    SendMailToSelectionOfBookersInput,
    SendMailToSelectionOfBookingInput,
    SendMailToSelectionOfCourseInput,
    SendMailToSelectionOfStaffInput,
    SendMailToSelectionOfScheduledPaymentsInput,
    useSendEmailToSelectionOfAttendeesMutation,
    useSendEmailToSelectionOfBookersMutation,
    useSendEmailToSelectionOfBookingsMutation,
    useSendEmailToSelectionOfCourseMutation,
    useSendEmailToSelectionOfStaffMutation,
    useSendEmailToSelectionOfScheduledPaymentsMutation,
    useSendInvoicesToSelectionOfBookingsMutation,
    GetCourseDetailsByIdDocument,
} from "../../../../generated/graphql";
import {SelectionType} from "../EmailTemplatePicker";
import {apolloClient} from "../../../common/ApolloWrapper";
import {KnownError, handleError} from "../../../../errors/handleErrors";

const knownErrors: Array<KnownError> = [
    {
        key: "Payload Too Large",
        title: "Der Anhang ist zu groß",
        msg: "Anhänge können maximal 15 MB groß sein. Es werden die Anhänge der zu sendenden Emails zusammengezählt.",
    },
    {
        key: "default",
        title: "Fehler beim Senden der Emails",
        msg: "Es ist ein Fehler beim Senden der Emails aufgetreten. Es wurden keine Emails versendet.",
    },
];

type NotifySelection = "notifyBookings" | "notifyWaitlist" | "notifyCancelations";

type SendToCourseProps = {
    selection: Array<SelectionType>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfCourseInput["attachments"];
    disabledSendButton?: boolean;
    notifySelection?: Array<NotifySelection>;
    attachInvoices?: boolean;
    attachReversalInvoices?: boolean;
};

export const SendToCourse: FC<SendToCourseProps> = ({
    selection,
    dbTemplateId,
    editedTemplateInput,
    closeAll,
    attachments,
    disabledSendButton,
    notifySelection,
    attachInvoices,
    attachReversalInvoices,
}) => {
    const sendMailToSelectionOfCourseInput: SendMailToSelectionOfCourseInput = {
        courseId: selection[0].id,
        templateId: dbTemplateId ?? "",
        notifyBookings: notifySelection?.includes("notifyBookings"),
        notifyWaitlist: notifySelection?.includes("notifyWaitlist"),
        notifyCancelations: notifySelection?.includes("notifyCancelations"),
        editedTemplateInput,
        attachments,
        attachInvoices: attachInvoices ?? undefined,
        attachReversalInvoices: attachReversalInvoices ?? undefined,
    };

    // console.log(
    //     "sendMailToSelectionOfCourseInput",
    //     sendMailToSelectionOfCourseInput,
    // );

    const [sendMail, {loading}] = useSendEmailToSelectionOfCourseMutation({
        client: apolloClient,
    });

    const handleSend = async (sendMailToSelectionOfCourseInput: SendMailToSelectionOfCourseInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            await sendMail({
                variables: {sendMailToSelectionOfCourseInput},
                refetchQueries: [
                    "GetCoursesWithPagination",
                    "GetBookingsByCourse",
                    {
                        query: GetBookingsByCourseDocument,
                        variables: {id: selection[0].id},
                    },
                ],
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendEmailsToSelectionOfCourse?.body.Messages.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend Course => sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfCourseInput", error, "modal", knownErrors);
            // message.error({
            //     key: "sendingMails",
            //     content: `Emails konnten nicht versendet werden: ${error}`,
            //     duration: 2,
            //     then: () => {
            //         message.destroy("sendingMails");
            //     },
            // });
        } finally {
            closeAll?.();
        }
    };

    const btnText = editedTemplateInput ? "Senden ohne speichern" : "An Kurs senden";

    return (
        <Button type="primary" onClick={async () => handleSend(sendMailToSelectionOfCourseInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
            {btnText}
        </Button>
    );
};

type SendToBookingProps = {
    selection: Array<Booking>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfBookingInput["attachments"];
    disabledSendButton?: boolean;
    attachInvoices?: boolean;
    attachReversalInvoices?: boolean;
};

export const SendToBooking: FC<SendToBookingProps> = ({selection, dbTemplateId, editedTemplateInput, closeAll, attachments, disabledSendButton, attachInvoices, attachReversalInvoices}) => {
    const sendMailToSelectionOfBookingInput: SendMailToSelectionOfBookingInput = {
        bookingIds: selection.map((booking) => booking.id),
        templateId: dbTemplateId ?? "",
        editedTemplateInput,
        attachments,
        attachInvoices: attachInvoices ?? undefined,
        attachReversalInvoices: attachReversalInvoices ?? undefined,
    };

    const [sendMail, {loading}] = useSendEmailToSelectionOfBookingsMutation({
        client: apolloClient,
    });

    const handleSend = async (sendMailToSelectionOfBookingInput: SendMailToSelectionOfBookingInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            const courseId =
                selection[0]?.courseId ?? selection[0]?.courseLessons?.[0]?.course?.id ?? "";

            await sendMail({
                variables: {sendMailToSelectionOfBookingInput},
                refetchQueries: [
                    "GetCoursesWithPagination",
                    "GetUnsettledBookings",
                    "GetSettledBookings",
                    "GetBookingsByCourse",
                    {
                        query: GetBookingsByCourseDocument,
                        variables: {id: courseId},
                    },
                ],
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendMailToSelectionOfBookings?.body?.Messages?.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend Booking => sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfCourseInput", error, "modal", knownErrors);
            // message.error({
            //     key: "sendingMails",
            //     content: `Emails konnten nicht versendet werden: ${error}`,
            //     duration: 2,
            //     then: () => {
            //         message.destroy("sendingMails");
            //     },
            // });
        } finally {
            closeAll?.();
        }
    };

    const btnText = editedTemplateInput ? "Senden ohne speichern" : "An Buchungen senden";

    return (
        <Button type="primary" onClick={async () => handleSend(sendMailToSelectionOfBookingInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
            {btnText}
        </Button>
    );
};

type SendToBookersProps = {
    selection: Array<SelectionType>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfBookingInput["attachments"];
    disabledSendButton?: boolean;
};

export const SendToBookers: FC<SendToBookersProps> = ({selection, dbTemplateId, editedTemplateInput, closeAll, attachments, disabledSendButton}) => {
    const sendMailToSelectionOfBookersInput: SendMailToSelectionOfBookersInput = {
        bookerIds: selection.map((booker) => booker.id),
        templateId: dbTemplateId ?? "",
        editedTemplateInput,
        attachments,
    };

    const [sendMail, {loading}] = useSendEmailToSelectionOfBookersMutation({
        client: apolloClient,
        refetchQueries: ["GetBookersOverview"],
    });

    const handleSend = async (sendMailToSelectionOfBookersInput: SendMailToSelectionOfBookersInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            await sendMail({
                variables: {sendMailToSelectionOfBookersInput},
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendEmailToSelectionOfBookers?.body?.Messages?.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend Bookers=> sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfCourseInput", error, "modal", knownErrors);
            // message.error({
            //     key: "sendingMails",
            //     content: `Emails konnten nicht versendet werden: ${error}`,
            //     duration: 2,
            //     then: () => {
            //         message.destroy("sendingMails");
            //     },
            // });
        } finally {
            closeAll?.();
        }
    };

    const btnText = editedTemplateInput ? "Senden ohne speichern" : "An Bucher senden";

    return (
        <Button type="primary" onClick={async () => handleSend(sendMailToSelectionOfBookersInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
            {btnText}
        </Button>
    );
};

type SendToAttendeesProps = {
    selection: Array<SelectionType>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfBookingInput["attachments"];
    disabledSendButton?: boolean;
};

export const SendToAttendees: FC<SendToAttendeesProps> = ({selection, dbTemplateId, editedTemplateInput, closeAll, attachments, disabledSendButton}) => {
    const sendMailToSelectionOfAttendeesInput: SendMailToSelectionOfAttendeesInput = {
        attendeeIds: selection.map((attendee) => attendee.id),
        templateId: dbTemplateId ?? "",
        editedTemplateInput,
        attachments,
    };

    const [sendMail, {loading}] = useSendEmailToSelectionOfAttendeesMutation({
        client: apolloClient,
        refetchQueries: ["AttendeeOverviewGetAttendeesMinimal"],
    });

    const handleSend = async (sendMailToSelectionOfAttendeesInput: SendMailToSelectionOfAttendeesInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            await sendMail({
                variables: {sendMailToSelectionOfAttendeesInput},
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendEmailToSelectionOfAttendees?.body?.Messages?.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend Attendees => sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfCourseInput", error, "modal", knownErrors);
            // message.error({
            //     key: "sendingMails",
            //     content: `Emails konnten nicht versendet werden: ${error}`,
            //     duration: 2,
            //     then: () => {
            //         message.destroy("sendingMails");
            //     },
            // });
        } finally {
            closeAll?.();
        }
    };

    const btnText = editedTemplateInput ? "Senden ohne speichern" : "An Bucher senden";

    return (
        <Button type="primary" onClick={async () => handleSend(sendMailToSelectionOfAttendeesInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
            {btnText}
        </Button>
    );
};

type SendToScheduledPaymentsProps = {
    selection: Array<SelectionType>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfBookingInput["attachments"];
    disabledSendButton?: boolean;
    attachInvoices?: boolean;
    attachReversalInvoices?: boolean;
};

export const SendToScheduledPayments: FC<SendToScheduledPaymentsProps> = ({
    selection,
    dbTemplateId,
    editedTemplateInput,
    closeAll,
    attachments,
    disabledSendButton,
    attachInvoices,
    attachReversalInvoices,
}) => {
    const sendMailToSelectionOfScheduledPaymentsInput: SendMailToSelectionOfScheduledPaymentsInput = {
        scheduledPaymentIds: selection.map((scheduledPayment) => scheduledPayment.id),
        templateId: dbTemplateId ?? "",
        editedTemplateInput,
        attachments,
        attachInvoices: attachInvoices ?? undefined,
        attachReversalInvoices: attachReversalInvoices ?? undefined,
    };

    const [sendMail, {loading}] = useSendEmailToSelectionOfScheduledPaymentsMutation({
        client: apolloClient,
    });

    const handleSend = async (sendMailToSelectionOfScheduledPaymentsInput: SendMailToSelectionOfScheduledPaymentsInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            await sendMail({
                variables: {sendMailToSelectionOfScheduledPaymentsInput},
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendEmailToSelectionOfScheduledPayments?.body?.Messages?.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend ScheduledPayments => sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfCourseInput", error, "modal", knownErrors);
            // message.error({
            //     key: "sendingMails",
            //     content: `Emails konnten nicht versendet werden: ${error}`,
            //     duration: 2,
            //     then: () => {
            //         message.destroy("sendingMails");
            //     },
            // });
        } finally {
            closeAll?.();
        }
    };

    const btnText = editedTemplateInput ? "Senden ohne speichern" : "An Bucher des Postens senden";

    return (
        <Button type="primary" onClick={async () => handleSend(sendMailToSelectionOfScheduledPaymentsInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
            {btnText}
        </Button>
    );
};

type SendInvoiceToBookingsProps = {
    selection: Array<Booking>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfBookingInput["attachments"];
    disabledSendButton?: boolean;
};

export const SendInvoiceToBookings: FC<SendInvoiceToBookingsProps> = ({selection, dbTemplateId, editedTemplateInput, closeAll, attachments, disabledSendButton}) => {
    const sendInvoicesToSelectionOfBookingsInput: SendInvoicesToSelectionOfBookingInput = {
        editedTemplateInput,
        bookingIds: selection.map((booking) => booking.id),
        reversal: false,
    };

    const [sendMail, {loading}] = useSendInvoicesToSelectionOfBookingsMutation({
        client: apolloClient,
    });

    const handleSend = async (sendInvoicesToSelectionOfBookingsInput: SendInvoicesToSelectionOfBookingInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            const courseId =
                selection[0]?.courseId ?? selection[0]?.courseLessons?.[0]?.course?.id ?? "";

            await sendMail({
                variables: {sendInvoicesToSelectionOfBookingsInput},
                refetchQueries: [
                    "GetCoursesWithPagination",
                    "GetUnsettledBookings",
                    "GetSettledBookings",
                    "GetBookingsByCourse",
                    {
                        query: GetBookingsByCourseDocument,
                        variables: {id: courseId},
                    },
                    "GetCourseDetailsById",
                    {query: GetCourseDetailsByIdDocument, variables: {id: courseId}},
                ],
                awaitRefetchQueries: true,
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendInvoicesToSelectionOfBookings?.body?.Messages?.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend Invoice => sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfCourseInput", error, "modal", knownErrors);
        } finally {
            closeAll?.();
        }
    };

    const pluralSingular = selection.length > 1 ? "s" : "";
    const pluralSingularInvoice = selection.length > 1 ? "ungen" : "ung";

    const btnText = editedTemplateInput ? "Senden ohne speichern" : `Email${pluralSingular} mit Rechn${pluralSingularInvoice} senden`;

    const handleCloseAll = () => {
        closeAll?.();
    };

    return (
        <>
            <Button danger onClick={handleCloseAll}>
                {"Nicht senden"}
            </Button>
            <Button type="primary" onClick={async () => handleSend(sendInvoicesToSelectionOfBookingsInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
                {btnText}
            </Button>
        </>
    );
};

type SendMailToSelectionOfStaffProps = {
    selection: Array<SelectionType>;
    dbTemplateId?: string;
    editedTemplateInput?: EditTemplateInput;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfBookingInput["attachments"];
    disabledSendButton?: boolean;
};

export const SendMailToSelectionOfStaff: FC<SendMailToSelectionOfStaffProps> = ({selection, dbTemplateId, editedTemplateInput, closeAll, attachments, disabledSendButton}) => {
    const sendMailToSelectionOfStaffInput: SendMailToSelectionOfStaffInput = {
        staffIds: selection.map((staff) => staff.id),
        templateId: dbTemplateId ?? "",
        editedTemplateInput,
        attachments,
    };

    const [sendMail, {loading}] = useSendEmailToSelectionOfStaffMutation({
        client: apolloClient,
        refetchQueries: ["GetBookersOverview"],
    });

    const handleSend = async (sendMailToSelectionOfStaffInput: SendMailToSelectionOfStaffInput) => {
        try {
            message.loading({
                key: "sendingMails",
                content: "Emails werden versendet",
            });
            await sendMail({
                variables: {sendMailToSelectionOfStaffInput},
            }).then((res) => {
                console.log("res", res);
                const messageCount = res.data?.sendEmailToSelectionOfStaff?.body?.Messages?.length ?? 0;

                message.success({
                    key: "sendingMails",
                    content: `${messageCount} Emails wurden versendet`,
                    duration: 2,
                    closable: true,
                    then: () => {
                        message.destroy("sendingMails");
                    },
                });
            });
        } catch (error) {
            console.log("===> handleSend Staff=> sendMail error", error);
            console.log(error);
            console.log("stringified:::", JSON.stringify(error));
            await handleError("sendMailToSelectionOfStaffInput", error, "modal", knownErrors);
        } finally {
            closeAll?.();
        }
    };

    const btnText = "An Mitarbeiter:innen senden";

    return (
        <Button type="primary" onClick={async () => handleSend(sendMailToSelectionOfStaffInput)} icon={<SendOutlined />} disabled={disabledSendButton} loading={loading}>
            {btnText}
        </Button>
    );
};
